/**
 * Frontend TypeScript type definitions
 */

export enum UserRole {
  PLAYER = 'player',
  COACH = 'coach',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
  ACADEMY_ADMIN = 'academy_admin',
  EVALUATION_ADMIN = 'evaluation_admin',
  CONTEST_ADMIN = 'contest_admin',
  SPONSOR = 'sponsor',
}

export enum GenderType {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
}

export enum SkillLevel {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced',
  PROFESSIONAL = 'professional',
}

export enum AgeCategory {
  U9 = 'U9',
  U11 = 'U11',
  U13 = 'U13',
  U15 = 'U15',
  U17 = 'U17',
  U19 = 'U19',
  SENIOR = 'Senior',
}

export enum SpeedLevel {
  SLOW = 'slow',
  MEDIUM = 'medium',
  FAST = 'fast',
  VERY_FAST = 'very_fast',
}

export enum RankingCategory {
  SELF_RATED = 'self_rated',
  COACH_EVALUATED = 'coach_evaluated',
  PEER_COACH = 'peer_coach',
  MATCH_BASED = 'match_based',
  RALLY_TARGET = 'rally_target',
  SHOT_PERFORMANCE = 'shot_performance',
  COMPOSITE = 'composite',
}

export interface User {
  id: string;
  email: string;
  role: UserRole;
  is_email_verified: boolean;
  firstName?: string;
  lastName?: string;
  isActive?: boolean;
  // Coach specific fields
  coachId?: string;
  coachIdCode?: string;
  coachLevel?: string;
  // Academy (coach) fields
  academyId?: string;
  academyName?: string;
  academyDescription?: string;
  academyAddress?: string;
  academyContact?: string;
  // Player specific fields
  playerId?: string;
  playerIdCode?: string;
  loginMethod?: 'email' | 'player_id';
  canEditProfile?: boolean;
  canAddChildren?: boolean;
  canCreateChild?: boolean;
  maritalStatus?: 'individual' | 'married' | null;
  parentRole?: 'mother' | 'father' | 'guardian' | null;
  parentRoleSaved?: boolean;
  intendedChildCount?: number | null;
  needsFamilyOnboarding?: boolean;
  emailClaimed?: boolean;
  isChildInCustody?: boolean;
  handoffEligible?: boolean;
  gender?: string;
  dateOfBirth?: string;
  ageCategory?: string;
  children?: Array<{
    playerId: string;
    playerIdCode: string;
    firstName: string;
    lastName: string;
    gender?: string;
    dateOfBirth?: string;
    ageCategory?: string;
    consentVerified?: boolean;
    consentStatus?: 'verified' | 'pending';
    consentStatusLabel?: string;
    custodyStatus?: string;
    handoffEligible?: boolean;
  }>;
  // Sponsor specific fields
  sponsorId?: string;
  companyName?: string;
  profileStatus?: string;
  /** Academy Admin ID for school/academy login (contact email is not a login). */
  adminIdCode?: string;
  /** Set when DB user has is_super_admin=true (role may still be admin). */
  isSuperAdmin?: boolean;
  /** Yearly platform plan from login / /api/subscriptions/me. */
  subscription?: UserSubscription;
}

export type PlayerPlanId = 'basic' | 'multi' | 'elite';
export type SubscriptionCategory = 'individual' | 'family';
export type SubscriptionIntent = 'purchase' | 'prebook';
export type BetaAccessLevel = 'none' | 'limited' | 'full';
export type PaymentGateway = 'stripe' | 'razorpay';

export interface PrebookIntake {
  contact?: string;
  childrenToInclude?: number | null;
  sportsInterest?: string;
  sportIds?: string[];
  yearsLearning?: string;
  longHorizon?: string;
  ltadAwareness?: string;
  hoursPerDay?: string;
  wellbeing?: string;
}

export interface FamilyPlanConfig {
  planId: PlayerPlanId;
  category: SubscriptionCategory;
  sportIds: string[];
  sportsCount: number;
  childrenCount: number;
  extraChildren: number;
  publicProfile: boolean;
  featureAddOnIds: string[];
  /** @deprecated Use category === 'family'. Kept for stored mock checkouts. */
  familyPackage?: boolean;
  intent?: SubscriptionIntent;
  betaAccess?: BetaAccessLevel;
  startDate?: string | null;
  /** Parent intake captured at pre-book. Intent only — does not set plan sports. */
  prebookIntake?: PrebookIntake | null;
}

export interface UserSubscription {
  id?: string | null;
  offerId?: string | null;
  status: 'none' | 'active' | 'expired' | 'pending' | 'prebooked' | 'beta' | 'cancelled' | string;
  amountUsd?: number | null;
  currency?: string;
  billingPeriod?: string;
  provider?: string | null;
  providerRef?: string | null;
  startedAt?: string | null;
  expiresAt?: string | null;
  purchasable?: boolean;
  suggestedOfferId?: string | null;
  paymentMode?: string;
  /** Player/parent household dimensions stored at mock purchase. */
  planConfig?: FamilyPlanConfig | null;
}

export interface BillingInvoice {
  id: string;
  invoiceNumber: string;
  date: string;
  planLabel: string;
  description?: string;
  offerId?: string | null;
  amountUsd: number | null;
  currency: string;
  status: string;
  invoiceStatus?: string;
  kind: 'invoice' | 'reservation';
  billingPeriod?: string;
  provider?: string | null;
  providerRef?: string | null;
  startedAt?: string | null;
  expiresAt?: string | null;
  planConfig?: FamilyPlanConfig | null;
}

export interface PaymentMethodOnFile {
  id: string;
  gateway: PaymentGateway;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  holderName?: string | null;
  updatedAt?: string;
}

export interface PaymentIssue {
  id: string;
  issueType: string;
  description: string;
  gateway?: string | null;
  status: string;
  createdAt: string;
}

export interface Player {
  id: string;
  user_id: string;
  player_id_code: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: GenderType;
  phone?: string;
  age_category: string;
  skill_level: SkillLevel;
  playing_style?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  profile_photo_url?: string;
  // Parent information
  parentEmail?: string;
  parentPhone?: string;
  parentCountry?: string;
  fatherName?: string;
  motherName?: string;
  registeredByParent?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Coach {
  id: string;
  user_id: string;
  coach_id_code: string;
  first_name: string;
  last_name: string;
  phone?: string;
  bio?: string;
  years_of_experience?: number;
  certifications?: any[];
  specializations?: string;
  academy_name?: string;
  is_verified: boolean;
  verification_date?: string;
  profile_photo_url?: string;
  created_at: string;
  updated_at: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  parentalConsentPending?: boolean;
  playerIdCode?: string;
  parentEmail?: string;
  emailVerificationRequired?: boolean;
  verificationLink?: string;
}

export interface RegisterPlayerRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  gender: GenderType;
  phone?: string;
  country?: string;
  is_below_18?: boolean;
  registered_by_parent?: boolean;
  parental_attestation?: boolean;
  parent_email?: string;
  parent_phone?: string;
  parent_country?: string;
  father_name?: string;
  mother_name?: string;
  guardian_name?: string;
  guardian_email?: string;
  country?: string;
  registered_by_parent?: boolean;
  parental_attestation?: boolean;
  parent_first_name?: string;
  parent_last_name?: string;
  parent_date_of_birth?: string;
  parent_gender?: GenderType;
  registrant_relationship?: 'parent' | 'guardian';
  consent_terms?: boolean;
  consent_data_processing?: boolean;
  consent_marketing?: boolean;
}

export interface RegisterCoachRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  bio?: string;
  years_of_experience?: number;
  certifications?: any[];
  specializations?: string;
  academy_name?: string;
}

export interface RegisterSponsorRequest {
  email: string;
  password: string;
  companyName: string;
  contactName: string;
  phone?: string;
  websiteUrl?: string;
  country?: string;
  industry?: string;
  preferredTier?: string;
  budgetBand?: string;
  sports?: string[];
  interestCategories?: string[];
}

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  code?: string;
  data?: T;
  errors?: any;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface ShotEvaluation {
  id: string;
  player_id: string;
  shot_type_id: string;
  evaluator_id?: string;
  session_date: string;
  session_type: 'live_practice' | 'video_submission';
  total_attempts: number;
  successful_attempts: number;
  failed_attempts: number;
  accuracy_score: number;
  speed_level: SpeedLevel;
  speed_score: number;
  overall_score?: number;
  video_url?: string;
  is_validated: boolean;
  validated_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface Tournament {
  id: string;
  name: string;
  description?: string;
  tournament_type: 'singles' | 'doubles' | 'mixed_doubles';
  age_category: string;
  start_date: string;
  end_date: string;
  registration_start_date: string;
  registration_end_date: string;
  venue?: string;
  max_participants?: number;
  match_format?: string;
  tournament_format: 'knockout' | 'round_robin' | 'hybrid';
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface PlayerRanking {
  id: string;
  player_id: string;
  ranking_category: RankingCategory;
  score: number;
  rank_position?: number;
  age_category: string;
  evaluated_by?: string;
  evaluation_date: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}


